<template>
  <div
    v-if="hasAccess"
    class="fill-height"
  >
    <v-row>
      <v-col
        cols="12"
        class="buttons__container"
      >
        <div class="buttons__time">
          <v-btn
            id="one_month"
            :class="{ primary: selection === 'one_day' }"
            @click="updateData('one_day')"
          >
            {{ $vuetify.lang.t('$vuetify.day') }}
          </v-btn>
          <v-btn
            id="one_week"
            :class="{ primary: selection === 'one_week' }"
            @click="updateData('one_week')"
          >
            {{ $vuetify.lang.t('$vuetify.week') }}
          </v-btn>
          <v-btn
            id="one_month"
            :class="{ primary: selection === 'one_month' }"
            @click="updateData('one_month')"
          >
            {{ $vuetify.lang.t('$vuetify.month') }}
          </v-btn>
          <v-btn
            id="one_year"
            :class="{ primary: selection === 'one_year' }"
            @click="updateData('one_year')"
          >
            {{ $vuetify.lang.t('$vuetify.year') }}
          </v-btn>
          <v-btn
            id="all"
            :class="{ primary: selection === 'all' }"
            @click="updateData('all')"
          >
            {{ $vuetify.lang.t('$vuetify.all') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <div
      v-for="(item, key) in chartData"
      :key="key"
      class="mb-4"
    >
      <div v-if="ready">
        <v-card :loading="loading">
          <v-card-title>
            {{ item.machineDisplayName }}
          </v-card-title>
          <v-chart
            type="area"
            height="350"
            :options="chartOptions[key] ? chartOptions[key] : defaultChartOptions"
            :series="item"
          />
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import moment from 'moment';
import lodash from 'lodash';
import invert from 'invert-color';
import apiUtil from '../utils/ApiUtil';

export default {
  components: {
    'v-chart': VueApexCharts,
  },
  data: () => {
    return {
      permissionsKey: 7,
      selection: 'one_day',
      ready: false,
      data: null,
      loading: false,
      annotationData: null,
      chartOptions: {},
      defaultChartOptions: {
        annotations: {
          xaxis: [],
        },
        colors: ['#81C784', '#E57373'],
        toolbar: {
          tools: {
            download: false,
          },
        },
        dataLabels: {
          enabled: false,
        },

        markers: {
          size: 0,
          style: 'hollow',
        },
        xaxis: {
          min: moment()
            .startOf('day')
            .valueOf(),
          max: moment()
            .endOf('day')
            .valueOf(),
          type: 'datetime',
          tickAmount: 6,
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          labels: {
            formatter: (value) => {
              return value ? 'on' : 'off';
            },
          },
          tickAmount: 1,
          min: 0,
          max: 1,
        },
        tooltip: {
          x: {
            format: 'dd MMM yyyy HH:mm:ss',
          },
        },
        fill: {
          type: 'solid',
        },
      },
    };
  },
  computed: {
    hasAccess() {
      return (
        this.$store.state.user.permissions.indexOf(this.permissionsKey) >= 0
      );
    },
    chartData() {
      if (!this.data) {
        return [];
      }
      const chartData = {};
      for (const i in this.data) {
        const microcontroller = this.data[i];
        if (!chartData[i]) {
          chartData[i] = {};
          chartData[i].machineDisplayName = microcontroller.machineDisplayName;
        }
        for (const item of microcontroller.on) {
          if (!chartData[i].on) {
            chartData[i].on = [];
          }

          chartData[i].on.push([moment(item.timestamp).valueOf(), item.on]);
        }

        for (const item of microcontroller.working) {
          if (!chartData[i].working) {
            chartData[i].working = [];
          }
          chartData[i].working.push([
            moment(item.timestamp).valueOf(),
            item.working,
          ]);
        }
      }

      const prepared = {};
      for (const [key, item] of Object.entries(chartData)) {
        prepared[key] = [
          { name: this.$vuetify.lang.t('$vuetify.workingStatus'), data: item.working },
          { name: this.$vuetify.lang.t('$vuetify.powerStatus'), data: item.on },
        ];
        prepared[key].machineDisplayName = item.machineDisplayName;
      }

      return prepared;
    },
  },
  async created() {
    this.$store.dispatch('setGlobalLoading', true);
    this.annotationData = await apiUtil.getAllAnnotationForms();
    await this.updateData('one_day');
    setTimeout(() => {
      this.ready = true;
      this.$store.dispatch('setGlobalLoading', false);
    }, 100);
  },
  methods: {
    async updateData(timeline) {
      this.selection = timeline;
      this.loading = true;
      switch (timeline) {
        case 'one_day':
          this.defaultChartOptions.xaxis.min = moment()
            .startOf('day')
            .valueOf();
          this.defaultChartOptions.xaxis.max = moment()
            .endOf('day')
            .valueOf();
          this.data = await apiUtil.getDailyGraphStat();
          break;
        case 'one_month':
          this.defaultChartOptions.xaxis.min = moment()
            .startOf('month')
            .valueOf();
          this.defaultChartOptions.xaxis.max = moment()
            .endOf('month')
            .valueOf();
          this.data = await apiUtil.getMonthlyGraphStat();
          break;
        case 'one_week':
          this.defaultChartOptions.xaxis.min = moment()
            .startOf('week')
            .valueOf();
          this.defaultChartOptions.xaxis.max = moment()
            .endOf('week')
            .valueOf();
          this.data = await apiUtil.getWeeklyGraphStat();
          break;
        case 'one_year':
          this.defaultChartOptions.xaxis.min = moment()
            .startOf('year')
            .valueOf();
          this.defaultChartOptions.xaxis.max = moment().valueOf();
          this.data = await apiUtil.getYearlyGraphStat();
          break;
        case 'all':
          this.defaultChartOptions.xaxis.min = undefined;
          this.defaultChartOptions.xaxis.max = undefined;
          this.data = await apiUtil.getAllGraphStat();
          break;
        default:
      }
      this.buildChartOptions();
      // TODO: Workaround force redrawing of the whole chart, because chart options are not reactive.
      this.forceGraphRedraw();
      this.loading = false;
    },
    buildChartOptions() {
      // happens if the user has no annotation permission
      if ('success' in this.annotationData && this.annotationData.success === false) {
        return;
      }

      for (const annotationKey in this.annotationData) {
        const annotationItem = this.annotationData[annotationKey];
        this.chartOptions[annotationKey] = lodash.cloneDeep(this.defaultChartOptions);
        for (const item of annotationItem) {
          this.chartOptions[annotationKey].annotations.xaxis.push({
            x: moment(item.timestamp).valueOf(),
            label: {
              show: true,
              text: item.name,
              style: {
                color: invert(item.color, true),
                background: item.color,
              },
            },
          });
        }
      }
    },
    forceGraphRedraw() {
      this.ready = false;
      setTimeout(() => { this.ready = true; }, 100);
    },
  },
};
</script>
